import { type BufferGeometry, DynamicDrawUsage, type Group, InstancedMesh, type Material, Matrix4, Quaternion, Vector3 } from 'three';
import type { PixelCamera } from '../../camera/PixelCamera';

/*
 * Instanced batch machinery. Every effect is an instance in some batch, one batch per
 * palette colour and shape, rebuilt from scratch each frame: a colour is never invented
 * here and a busy firefight costs a handful of draw calls.
 */

export const TAU = Math.PI * 2;
export const QUARTER_TURN = Math.PI / 4;
export const IDENTITY = new Quaternion();

/** One InstancedMesh plus the write cursor for the frame being assembled. */
export interface Batch {
  readonly mesh: InstancedMesh;
  readonly capacity: number;
  /** Persistent batches (scorch marks) are written on demand, not rebuilt every frame. */
  readonly persistent: boolean;
  cursor: number;
}

/* Scratch objects: the per-frame path allocates nothing of its own. */
const _pos = new Vector3();
const _scale = new Vector3();
const _quat = new Quaternion();
const _mat = new Matrix4();

export function createBatch(
  root: Group,
  geometry: BufferGeometry,
  material: Material,
  capacity: number,
  castShadow: boolean,
  receiveShadow: boolean,
  persistent: boolean,
): Batch {
  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.count = 0;
  mesh.visible = false;
  // Instances roam the whole map; a bounding sphere computed once would cull them wrongly.
  mesh.frustumCulled = false;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  root.add(mesh);
  return { mesh, capacity, persistent, cursor: 0 };
}

/** Shared scratch quaternion turned about +y: valid until the next `yawQuat` or `axisQuat`. */
export function yawQuat(yaw: number): Quaternion {
  return _quat.set(0, Math.sin(yaw * 0.5), 0, Math.cos(yaw * 0.5));
}

/** The same scratch quaternion, turned about an arbitrary unit axis. */
export function axisQuat(axis: Vector3, angle: number): Quaternion {
  return _quat.setFromAxisAngle(axis, angle);
}

/** Append one instance. Pass the camera for anything that moves, so it is texel-locked (05 §6.2). */
export function put(
  batch: Batch,
  camera: PixelCamera | null,
  x: number,
  y: number,
  z: number,
  sx: number,
  sy: number,
  sz: number,
  q: Quaternion,
): void {
  if (batch.cursor >= batch.capacity) return;
  _pos.set(x, y, z);
  if (camera) camera.snapToTexel(_pos, _pos);
  _scale.set(sx, sy, sz);
  batch.mesh.setMatrixAt(batch.cursor, _mat.compose(_pos, q, _scale));
  batch.cursor++;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Start a frame: every per-frame batch is emptied and refilled by whoever has something to show. */
export function resetBatches(batches: readonly Batch[]): void {
  for (const batch of batches) if (!batch.persistent) batch.cursor = 0;
}

/** End a frame: upload only the instances written, and hide batches with nothing in them. */
export function flushBatches(batches: readonly Batch[]): void {
  for (const batch of batches) {
    if (batch.persistent) continue;
    if (batch.cursor > 0) {
      const attr = batch.mesh.instanceMatrix;
      attr.clearUpdateRanges();
      attr.addUpdateRange(0, batch.cursor * 16);
      attr.needsUpdate = true;
    }
    batch.mesh.count = batch.cursor;
    batch.mesh.visible = batch.cursor > 0;
  }
}

export function disposeBatches(batches: readonly Batch[]): void {
  for (const batch of batches) {
    batch.mesh.geometry.dispose();
    batch.mesh.dispose();
  }
}
